/* global window, document, navigator */
import React, { Fragment, } from 'react';
import PropTypes from 'prop-types';
import { createComponent, } from 'react-fela';
import { parseStyleProps, } from '@haaretz/htz-css-tools';

import ApolloConsumer from '../ApolloBoundary/ApolloConsumer';
import { stylesPropType, } from '../../propTypes/stylesPropType';

const propTypes = {
  /**
   * The duration of the sliding animation.
   */
  animationDuration: PropTypes.number,
  /**
   * How many items should be rendered in the carousel's view.
   */
  inView: PropTypes.number,
  /**
   * The length property of the items array.
   */
  itemsLength: PropTypes.number.isRequired,
  /**
   * Should the carousel iterate over the given items in a loop, or start at 0 and end with n.
   */
  loop: PropTypes.bool,
  /**
   * A special property holding miscellaneous CSS values that
   * trumps all default values. Processed by
   * [`parseStyleProps`](https://Haaretz.github.io/htz-frontend/htz-css-tools#parsestyleprops)
   */
  miscStyles: stylesPropType,
  /**
   * A render prop  that passes down the Carousel parts' render functions.
   */
  render: PropTypes.func.isRequired,
  /**
   * At which position in the item's array (index) should the carousel start at mounting time.
   */
  startAt: PropTypes.number,
  /**
   * How many steps should the carousel iterate over the items list, whenever a next/previous button is fired.
   */
  step: PropTypes.number,
  /**
   * Should the behaviour fit left-to-right document direction (false by default)
   */
  isLtr: PropTypes.bool,
};

const defaultProps = {
  animationDuration: 0,
  inView: 1,
  loop: false,
  miscStyles: null,
  startAt: 0,
  step: 1,
  isLtr: false,
};

const keysToDirection = {
  rtl: { 37: 'next', 39: 'previous', },
  ltr: { 37: 'previous', 39: 'next', },
};

const sideToDirection = {
  rtl: { right: 'next', left: 'previous', },
  ltr: { right: 'previous', left: 'next', },
};

const wrapperStyle = ({ miscStyles, theme, }) => ({
  extend: [
    ...(miscStyles ? parseStyleProps(miscStyles, theme.mq, theme.type) : []),
  ],
});
const ItemsWrapper = createComponent(wrapperStyle);

const itemsStyle = ({ theme, position, moving, animationDuration, isLtr, miscStyles, }) => ({
  height: '100%',
  position: 'absolute',
  top: '0',
  transform: `translateX(${isLtr ? -position : position}%)`,
  width: '100%',
  ...(moving ? {
    transitionProperty: 'all',
    ...theme.getDuration('transition', animationDuration),
    ...theme.getTimingFunction('transition', 'swiftOut'),
  } : {}),
  extend: [
    ...(miscStyles ? parseStyleProps(miscStyles, theme.mq, theme.type) : []),
  ],
});
const Items = createComponent(itemsStyle, 'div', ({ miscStyles, animationDuration, isLtr, moving, ...props }) => Object.keys(props));

const currentItemsStyle = ({ theme, moving, direction, miscStyles, }) => ({
  position: 'static',
  zIndex: 1,
  extend: [
    ...(miscStyles ? parseStyleProps(miscStyles, theme.mq, theme.type) : []),
  ],
});
const CurrentItems = createComponent(currentItemsStyle, Items, ({ miscStyles, animationDuration, isLtr, moving, ...props }) => Object.keys(props)
);

/**
 * The Carousel component takes an array of objects and a renderer component, and renders
 * to its view **only** the amount of items specified by the props, and some extra items from
 * each side of its view, according to the settings given to it (loop, step, inView, etc).
 */
class Carousel extends React.Component {
  state = {
    displayItemNum: this.props.startAt,
    moving: false,
    direction: null,
    startTouchX: null,
    startTouchY: null,
  };

  componentDidMount() {
    this.wrapper.addEventListener(
      'touchstart',
      event => {
        const startTouchX = event.touches[0].clientX;
        const startTouchY = event.touches[0].clientY;
        this.setState({
          startTouchX,
          startTouchY,
        });
      },
      false
    );
    this.wrapper.addEventListener(
      'touchend',
      event => {
        const endTouchX = event.changedTouches[0].clientX;
        const endTouchY = event.changedTouches[0].clientY;

        const isSwipeX = Math.abs(endTouchX - this.state.startTouchX)
          > Math.abs(endTouchY - this.state.startTouchY);

        if (isSwipeX && endTouchX > this.state.startTouchX) {
          this.changeItem(sideToDirection[this.props.isLtr ? 'ltr' : 'rtl'].right);
        }
        else if (isSwipeX && endTouchX < this.state.startTouchX) {
          this.changeItem(sideToDirection[this.props.isLtr ? 'ltr' : 'rtl'].left);
        }
      },
      false
    );
    this.currentItems.addEventListener(
      this.getTransitionEnd(),
      this.changeState,
      false
    );
    document.addEventListener('keydown', this.handleGlobalKeydown);
  }

  shouldComponentUpdate(nextProps, nextState) {
    return (
      this.state.displayItemNum !== nextState.displayItemNum
      || this.state.moving !== nextState.moving
    );
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.handleGlobalKeydown);
  }

  getIndex = pos => {
    const current = this.state.displayItemNum;
    const total = this.props.itemsLength;
    const { step, loop, } = this.props;

    let index;

    if (pos === 'next') {
      if (current + step < total) {
        index = current + step;
      }
      else if (loop) {
        index = current + (step - total);
      }
      else {
        index = total;
      }
    }
    else if (current - step >= 0) {
      index = current - step;
    }
    else if (loop) {
      index = total + (current - step);
    }
    else index = 0;

    return index;
  };

  getTransitionEnd = () => {
    if ('ontransitionend' in window) {
      // Firefox
      return 'transitionend';
    }
    if ('onwebkittransitionend' in window) {
      // Chrome/Saf (+ Mobile Saf)/Android
      return 'webkitTransitionEnd';
    }
    if (
      'onotransitionend' in this.currentItems
      || navigator.appName === 'Opera'
    ) {
      // Opera
      // As of Opera 10.61, there is no "onotransitionend" property added to DOM elements,
      // so it will always use the navigator.appName fallback
      return 'oTransitionEnd';
    }
    // IE - not implemented (even in IE9) :(
    return false;
  };

  // TODO: remove this when check is done and everything works
  // handleGlobalClick = e => {
  //   this.wrapper && this.wrapper.contains(e.target)
  //     ? document.addEventListener('keydown', this.handleGlobalKeydown)
  //     : document.removeEventListener('keydown', this.handleGlobalKeydown);
  // };

  handleGlobalKeydown = e => {
    const key = e.which || e.keyCode;
    const direction = keysToDirection[this.props.isLtr ? 'ltr' : 'rtl'][key];
    if (direction) this.changeItem(direction);
  };

  changeItem = direction => {
    this.setState({
      moving: true,
      direction,
    });
  };

  changeState = () => {
    const newDisplay = this.state.moving
      ? this.getIndex(this.state.direction)
      : null;
    if (newDisplay !== null) {
      this.setState({
        displayItemNum: newDisplay,
        moving: false,
        direction: null,
      });
    }
  };

  render() {
    const {
      animationDuration,
      inView, // eslint-disable-line no-unused-vars
      itemsLength,
      loop,
      miscStyles,
      render,
      startAt, // eslint-disable-line no-unused-vars
      isLtr,
    } = this.props;

    const positionChange = this.state.direction === 'next'
      ? 100
      : this.state.direction === 'previous' ? -100 : 0;

    const renderPreviousItems = (itemsRenderer, miscStyles) => (
      this.state.displayItemNum > 0 || loop) && (
        <Items
          position={100 + positionChange}
          isLtr={isLtr}
          moving={this.state.moving}
          animationDuration={animationDuration}
          miscStyles={miscStyles}
          data-test="previousItem"
        >
          {itemsRenderer({ itemIndex: this.getIndex('prev'), })}
        </Items>
    );

    const renderNextItems = (itemsRenderer, miscStyles) => (
      (this.state.displayItemNum < itemsLength - 1 || loop) ? (
        <Items
          position={-100 + positionChange}
          isLtr={isLtr}
          moving={this.state.moving}
          animationDuration={animationDuration}
          miscStyles={miscStyles}
          data-test="nextItem"
        >
          {itemsRenderer({ itemIndex: this.getIndex('next'), })}
        </Items>
      ) : null
    );

    const renderCurrentItems = (ariaText, itemsRenderer, miscStyles) => (
      <ApolloConsumer>
        {cache => {
          cache.writeData({
            data: {
              ariaLive: {
                politeMessage: ariaText || '',
                __typename: 'AriaLive',
              },
            },
          });
          return (
            <CurrentItems
              animationDuration={animationDuration}
              position={positionChange}
              moving={this.state.moving}
              // eslint-disable-next-line no-return-assign
              innerRef={currentItems => (this.currentItems = currentItems)}
              miscStyles={miscStyles}
              isLtr={isLtr}
              data-test="currentItem"
            >
              {itemsRenderer({ itemIndex: this.state.displayItemNum, })}
            </CurrentItems>
          );
        }}
      </ApolloConsumer>
    );

    const renderButton = buttonRenderer => buttonRenderer({ changeItem: this.changeItem, });

    const renderIndicator = indicatorRenderer => indicatorRenderer();
    return (
      <Fragment>
        <ItemsWrapper
          innerRef={wrapper => (this.wrapper = wrapper)} // eslint-disable-line no-return-assign
          miscStyles={miscStyles}
          tabindex="0"
          data-test="carousel"
        >
          {render({
            renderPreviousItems,
            renderNextItems,
            renderCurrentItems,
            renderButton,
            renderIndicator,
            displayItemNum: this.state.displayItemNum,
            direction: this.state.direction,
            moving: this.state.moving,
            previousItemIndex: this.getIndex('prev'),
            nextItemIndex: this.getIndex('next'),
          })}
        </ItemsWrapper>
      </Fragment>
    );
  }
}

Carousel.propTypes = propTypes;
Carousel.defaultProps = defaultProps;

export default Carousel;
